<template>
  <div class="container-nzidoo">
    <page-title :title="$t('menu.list.users')" />

    <!-- Toolbar -->
    <div class="admin-toolbar">
      <div class="admin-search-box">
        <i class="mdi mdi-magnify admin-search-icon"></i>

        <input type="text" v-model="searchTerm" :placeholder="$t('sentence.searchByName')" class="admin-search-input">

        <!-- Clear icon -->
        <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"
          :title="$t('sentence.clear')"></i>
      </div>
    </div>

    <LoadingSpinner v-if="loader" />
    <!-- Users Grid -->
    <div class="admin-grid">
      <div v-for="user in filteredUsers" :key="user.id" class="admin-card">
        <div class="admin-card-header">
          <div class="admin-list-status">
            <span v-if="user.is_active == 1" class="badge badge-success">
              {{ $t("status.active") }}
            </span>
            <span v-else class="badge badge-danger">
              {{ $t("status.inactive") }}
            </span>
            <span v-if="user.roles[0] == 'ROLE_SELLER'" class="badge badge-info">
              {{ $t("actor.pro") }}
            </span>
            <span v-else class="badge badge-info">
              {{ $t("actor.individual") }}
            </span>
          </div>
          <div class="admin-card-actions">
            <button class="btn btn-icon-action btn-primary" @click="getUserById(user.id)" :title="$t('action.view')">
              <i class="mdi mdi-eye"></i>
            </button>
            <button v-if="user.is_active == 1" class="btn btn-icon-action btn-warning" @click="disableUser(user.id)"
              :title="$t('action.disable')">
              <i class="mdi mdi-account-off"></i>
            </button>
            <button v-else class="btn btn-icon-action btn-success" @click="enableUser(user.id)"
              :title="$t('action.enable')">
              <i class="mdi mdi-account-check"></i>
            </button>
            <button class="btn btn-icon-action btn-danger" @click="removeUser(user.id)" :title="$t('action.delete')">
              <i class="mdi mdi-delete"></i>
            </button>
          </div>
        </div>
        <div class="admin-card-body">
          <div class="admin-card-section">
            <label class="admin-card-label">{{ $t("main.users") }}</label>
            <div class="card-name">
              {{ user.lastname }} {{ user.firstname }}
            </div>
          </div>
          <div class="admin-card-section">
            <label class="admin-card-label">{{ $t("attr.email") }}</label>
            <div class="card-light">
              <i class="mdi mdi-email-outline"></i>
              {{ user.email }}
            </div>
          </div>
          <div class="admin-card-section">
            <label class="admin-card-label">{{ $t("main.validated") }}</label>
            <div>
              <span v-if="user.validated_client == 1 || user.validated_seller == 1" class="badge badge-success">
                {{ $t("status.validated") }}
              </span>
              <span v-else-if="user.validated_client == null && user.validated_seller == null"
                class="badge badge-warning">
                {{ $t("status.notYetValidated") }}
              </span>
              <span v-else class="badge badge-danger">
                {{ $t("status.refused") }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredUsers.length === 0" class="admin-empty-state">
        <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
        <p class="admin-empty-text">
          {{ searchTerm ? $t("sentence.noResults") : $t("main.noUsers") }}
        </p>
      </div>
    </div>

    <!-- Drawer Backdrop -->
    <transition name="drawer-backdrop">
      <div v-if="showDrawer" class="admin-drawer-backdrop" @click="handleClose"></div>
    </transition>

    <!-- Drawer for User Details -->
    <transition name="drawer">
      <div v-if="showDrawer" class="admin-drawer-container">
        <div class="admin-drawer-header">
          <div>
            <h3 class="admin-drawer-title">{{ $t("main.users") }} {{ $t("general.details") }}</h3>
            <p class="admin-drawer-subtitle">
              {{ user.lastname }} {{ user.firstname }}
            </p>
          </div>
          <button class="btn btn-close" @click="handleClose">
            <i class="mdi mdi-close"></i>
          </button>
        </div>

        <div class="admin-drawer-body">
          <!-- User Profile Picture -->
          <div class="user-profile-section">
            <div class="user-avatar-large">
              <img :src="user.picture == null ? userpng : config.storageUrl + user.picture" alt="user image" />
            </div>
            <div class="user-email-display">
              {{ user.email }}
            </div>

            <!-- Validation Actions -->
            <div v-if="user.validated_seller == null && user.validated_client == null" class="validation-actions">
              <p class="validation-message">{{ $t("sentence.waitingForApproval") }}</p>
              <div class="validation-buttons">
                <button @click="addRole(user.id, true)" class="btn btn-success">
                  <i class="mdi mdi-check"></i>
                  {{ $t("action.accept") }}
                </button>
                <button @click="addRole(user.id, false)" class="btn btn-danger">
                  <i class="mdi mdi-close"></i>
                  {{ $t("action.refuse") }}
                </button>
              </div>
            </div>
          </div>

          <hr class="drawer-divider" />

          <!-- User Information Grid -->
          <div class="user-info-grid">
            <!-- Profile Section -->
            <div class="info-section">
              <h5 class="info-section-title">{{ $t("attr.profile") }}</h5>

              <div class="info-item">
                <label class="info-label">{{ $t("attr.email") }}</label>
                <div class="info-value">{{ user.email }}</div>
              </div>

              <div class="info-item">
                <label class="info-label">{{ $t("attr.phone") }}</label>
                <div class="info-value">{{ user.phone || '-' }}</div>
              </div>

              <div class="info-item">
                <label class="info-label">{{ $t("attr.birthday") }}</label>
                <div class="info-value">{{ user.birthday || '-' }}</div>
              </div>

              <div class="info-item">
                <label class="info-label">{{ $t("attr.address") }}</label>
                <div class="info-value">
                  {{ user.address }} {{ user.postal_code }} {{ user.city }} {{ user.country }}
                </div>
              </div>
            </div>

            <!-- Client Info Section -->
            <div class="info-section">
              <h5 class="info-section-title">{{ $t("sentence.clientInfo") }}</h5>

              <div class="info-item">
                <label class="info-label">{{ $t("ecommerce.cardId") }}</label>
                <div v-if="user.card_id !== null" class="document-preview">
                  <img class="document-thumbnail" @click="showImage(config.storageUrl + user.card_id)"
                    :src="config.storageUrl + user.card_id" />
                  <button @click="showImage(config.storageUrl + user.card_id)" class="btn btn-sm btn-primary">
                    <i class="mdi mdi-eye"></i> {{ $t("action.view") }}
                  </button>
                </div>
                <div v-else class="no-document">
                  {{ $t("sentence.noFileUploadedYet") }}
                </div>
              </div>
            </div>

            <!-- Professional Info Section -->
            <div class="info-section">
              <h5 class="info-section-title">{{ $t("sentence.clientPro") }}</h5>

              <div class="info-item">
                <label class="info-label">{{ $t("ecommerce.rc") }}</label>
                <div v-if="user.rc !== null" class="document-preview">
                  <img class="document-thumbnail" @click="showImage(config.storageUrl + user.rc)"
                    :src="config.storageUrl + user.rc" />
                  <button @click="showImage(config.storageUrl + user.rc)" class="btn btn-sm btn-primary">
                    <i class="mdi mdi-eye"></i> {{ $t("action.view") }}
                  </button>
                </div>
                <div v-else class="no-document">
                  {{ $t("sentence.noFileUploadedYet") }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="admin-drawer-footer">
          <button @click="handleClose" class="btn btn-secondary">
            {{ $t("action.close") }}
          </button>
        </div>
      </div>
    </transition>

    <!-- Picture Modal -->
    <ProductDetailsModal v-if="selectedPicture" :picture="selectedPicture" />

    <!-- Loading Overlay -->
    <div v-if="loader" class="admin-loading-overlay">
      <div class="admin-spinner"></div>
    </div>

    <notifications style="top:5%;" width="35%" group="error" position="top center" />
    <notifications style="top:5%;" width="35%" group="success" position="top center" />
  </div>
</template>

<script>
import config from "@/config";
import ProductDetailsModal from "@/components/modals/PictureDetailsModal";
import PageTitle from '@/components/templates/PageTitle.vue';
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";
import userpng from '@/assets/img/user/user.webp';

export default {
  name: "manage-users",

  components: {
    ProductDetailsModal,
    LoadingSpinner,
    PageTitle
  },

  data() {
    return {
      userpng: userpng,
      users: [],
      user: {},
      searchTerm: "",
      showDrawer: false,
      loader: false,
      config: config,
      selectedPicture: null,
    };
  },

  computed: {
    filteredUsers() {
      if (!this.searchTerm) return this.users;

      const term=this.searchTerm.toLowerCase();
      return this.users.filter(user => {
        const fullName=`${user.firstname} ${user.lastname} ${user.email}`.toLowerCase();
        return fullName.includes(term);
      });
    }
  },

  mounted() {
    this.getUsers();
    if (undefined!==this.$route.params.id) {
      this.getUserById(this.$route.params.id);
    }
  },

  methods: {
    clearSearch() {
      this.searchTerm="";
    },
    showImage(picture) {
      this.selectedPicture=picture;
      this.$modal.show("picture-details");
    },

    getUsers() {
      const roles=["ROLE_USER", "ROLE_CLIENT", "ROLE_SELLER"];
      const self=this;
      this.loader=true;
      this.$store
        .dispatch("getUsers", { roles })
        .then((response) => {
          self.users=response.data;
          this.loader=false;
        })
        .catch((err) => {
          console.error("Error fetching users:", err);
          this.$notify({
            group: "error",
            title: err.response?.data||"Error fetching users",
            type: "error",
          });
        });
    },

    getUserById(userId) {
      const self=this;
      const roles="ROLE_ADMIN";
      this.loader=true;
      this.$axios({
        url: "admin/list",
        method: "POST",
        data: { userId: userId, roles: roles },
      })
        .then((response) => {
          self.user=response.data;
          self.showDrawer=true;
          self.loader=false;
        })
        .catch((err) => {
          console.error("Error fetching user:", err);
          this.$notify({
            group: "error",
            title: err.response?.data||"Error fetching user",
            type: "error",
          });
          self.loader=false;
        });
    },

    disableUser(userId) {
      const self=this;
      this.$axios({
        url: "admin/disable/user/"+userId,
        method: "POST",
      })
        .then((res) => {
          self.getUsers();
          this.$notify({
            group: "success",
            title: res.data.message||"User disabled successfully",
            type: "success",
          });
        })
        .catch((err) => {
          console.error("Error disabling user:", err);
          this.$notify({
            group: "error",
            title: err.response?.data||"Error disabling user",
            type: "error",
          });
        });
    },

    enableUser(userId) {
      const self=this;
      this.$axios({
        url: "admin/enable/user/"+userId,
        method: "POST",
      })
        .then((res) => {
          self.getUsers();
          if (self.showDrawer&&self.user.id===userId) {
            self.handleClose();
          }
          this.$notify({
            group: "success",
            title: res.data.message||"User enabled successfully",
            type: "success",
          });
        })
        .catch((err) => {
          console.error("Error enabling user:", err);
          this.$notify({
            group: "error",
            title: err.response?.data||"Error enabling user",
            type: "error",
          });
        });
    },

    removeUser(userId) {
      const self=this;
      this.$axios({
        url: "admin/remove/user/"+userId,
        method: "POST",
      })
        .then((res) => {
          self.getUsers();
          if (self.showDrawer&&self.user.id===userId) {
            self.handleClose();
          }
          this.$notify({
            group: "success",
            title: res.data.message||"User removed successfully",
            type: "success",
          });
        })
        .catch((err) => {
          console.error("Error removing user:", err);
          this.$notify({
            group: "error",
            title: err.response?.data||"Error removing user",
            type: "error",
          });
        });
    },

    addRole(userId, validated) {
      let role;

      if (this.user.rc!==null) {
        role=["ROLE_SELLER"];
        this.user.validated_seller=true;
      } else {
        role=["ROLE_CLIENT"];
        this.user.validated_client=true;
      }

      this.$axios({
        url: "admin/user/add/role",
        data: {
          id: userId,
          role: JSON.stringify(role),
          validated: validated,
        },
        method: "POST",
      })
        .then((res) => {
          this.getUsers();
          this.handleClose();
          this.$notify({
            group: "success",
            title: res.data.message||"Role assigned successfully",
            type: "success",
          });
        })
        .catch((err) => {
          console.error("Error assigning role:", err);
          this.$notify({
            group: "error",
            title: err.response?.data||"Error assigning role",
            type: "error",
          });
        });
    },

    handleClose() {
      this.showDrawer=false;
      this.user={};
    }
  },
};
</script>

<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>

/* Component-specific styles */
.admin-list-status {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* User Profile Section */
.user-profile-section {
  text-align: center;
  margin-bottom: 24px;
}

.user-avatar-large {
  width: 120px;
  height: 120px;
  margin: 0 auto 16px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid var(--gray-200);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.user-avatar-large img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-email-display {
  font-size: 16px;
  color: var(--gray-700);
  margin-bottom: 16px;
}

.validation-actions {
  margin-top: 20px;
}

.validation-message {
  font-size: 14px;
  color: var(--gray-600);
  margin-bottom: 12px;
}

.validation-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.drawer-divider {
  margin: 24px 0;
  border: 0;
  border-top: 1px solid var(--gray-200);
}

/* User Info Grid */
.user-info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

.info-section {
  background: var(--gray-50);
  padding: 20px;
  border-radius: 12px;
}

.info-section-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--gray-900);
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--gray-200);
}

.info-item {
  margin-bottom: 16px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--gray-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.info-value {
  font-size: 14px;
  color: var(--gray-700);
  word-break: break-word;
}

/* Document Preview */
.document-preview {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
}

.document-thumbnail {
  width: 100%;
  max-width: 200px;
  height: auto;
  border-radius: 8px;
  cursor: pointer;
  border: 2px solid var(--gray-200);
  transition: all 0.2s ease;
}

.document-thumbnail:hover {
  border-color: var(--color-nz);
  transform: scale(1.02);
}

.no-document {
  font-size: 14px;
  color: var(--gray-500);
  font-style: italic;
}

/* Responsive */
@media (min-width: 768px) {
  .user-info-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .user-info-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>